import {
  GetUnmatchedWorkTypesForTaskMoveDocument,
  type GetUnmatchedWorkTypesForTaskMoveQuery,
  type GetUnmatchedWorkTypesForTaskMoveQueryVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit/Select/types";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import {
  action,
  computed,
  makeObservable,
  type ObservableMap,
  observable,
} from "mobx";
import { createRef } from "react";

type WorkTypeInfo = {
  id: string;
  title: string;
};

export class PositionMappingStore {
  @observable.ref
  unmatchedWorkTypes: WorkTypeInfo[] = [];

  @observable.ref
  targetAvailableWorkTypes: WorkTypeInfo[] = [];

  @observable
  positionMapping: ObservableMap<string, string> = observable.map();

  @observable
  hasTimeTrackingEntries = false;

  wrapperRef = createRef<HTMLDivElement>();

  private fetcher = new FetchHelper<
    GetUnmatchedWorkTypesForTaskMoveQuery,
    GetUnmatchedWorkTypesForTaskMoveQueryVariables
  >(GetUnmatchedWorkTypesForTaskMoveDocument);

  constructor() {
    makeObservable(this);
  }

  @computed
  get isLoading(): boolean {
    return this.fetcher.isLoading.value;
  }

  @computed
  get hasUnmatchedPositions(): boolean {
    return this.unmatchedWorkTypes.length > 0;
  }

  @computed
  get allPositionsMapped(): boolean {
    if (!this.hasUnmatchedPositions) return true;
    return this.unmatchedWorkTypes.every(
      (wt) =>
        this.positionMapping.has(wt.id) && !!this.positionMapping.get(wt.id),
    );
  }

  @computed
  get targetWorkTypeOptions(): IOption[] {
    return this.targetAvailableWorkTypes.map((wt) => ({
      value: wt.id,
      label: wt.title,
    }));
  }

  @computed
  get serializedMapping():
    | Array<{
        old_time_tracking_work_type_id: string;
        new_time_tracking_work_type_id: string;
      }>
    | undefined {
    if (!this.hasUnmatchedPositions) return undefined;
    if (!this.allPositionsMapped) return undefined;
    return [...this.positionMapping.entries()].map(([oldId, newId]) => ({
      old_time_tracking_work_type_id: oldId,
      new_time_tracking_work_type_id: newId,
    }));
  }

  @action.bound
  async fetchUnmatched(taskIds: string[], newProjectId: string): Promise<void> {
    const [data, error] = await this.fetcher.fetch({
      taskIds,
      newProjectId,
    });

    if (error || !data) {
      this.unmatchedWorkTypes = [];
      this.targetAvailableWorkTypes = [];
      this.hasTimeTrackingEntries = false;
      return;
    }

    const result = data.getUnmatchedWorkTypesForTaskMove;
    this.unmatchedWorkTypes = result.unmatched_work_types;
    this.targetAvailableWorkTypes = result.target_available_work_types;
    this.hasTimeTrackingEntries = result.has_time_tracking_entries;
    this.positionMapping.clear();
  }

  @action.bound
  setMapping(oldWorkTypeId: string, newWorkTypeId: string): void {
    this.positionMapping.set(oldWorkTypeId, newWorkTypeId);
  }

  @action.bound
  reset(): void {
    this.unmatchedWorkTypes = [];
    this.targetAvailableWorkTypes = [];
    this.positionMapping.clear();
    this.hasTimeTrackingEntries = false;
  }
}
